(() => {
  const CartDrawer = {
    root: null,
    body: null,
    footer: null,
    countEls: null,

    init() {
      this.root = document.querySelector("[data-cart-drawer]");
      if (!this.root) return;

      this.body = this.root.querySelector("[data-cart-drawer-body]");
      this.footer = this.root.querySelector("[data-cart-drawer-footer]");
      this.countEls = document.querySelectorAll("[data-cart-count]");

      document.querySelectorAll("[data-cart-open]").forEach((el) => {
        el.addEventListener("click", (event) => {
          event.preventDefault();
          this.open();
        });
      });

      this.root.querySelectorAll("[data-cart-close]").forEach((el) => {
        el.addEventListener("click", () => this.close());
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.root.classList.contains("is-open")) {
          this.close();
        }
      });

      document.addEventListener("submit", async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.hasAttribute("data-product-form")) return;

        event.preventDefault();
        await this.addFromForm(form);
      });

      this.root.addEventListener("click", async (event) => {
        const removeBtn = event.target.closest("[data-cart-remove]");
        if (!removeBtn) return;
        event.preventDefault();
        const key = removeBtn.getAttribute("data-cart-remove");
        if (!key) return;
        await this.changeLine(key, 0);
      });
    },

    open() {
      this.root.classList.add("is-open");
      this.root.setAttribute("aria-hidden", "false");
      document.body.classList.add("cart-drawer-open");
      this.refresh();
    },

    close() {
      this.root.classList.remove("is-open");
      this.root.setAttribute("aria-hidden", "true");
      document.body.classList.remove("cart-drawer-open");
    },

    async addFromForm(form) {
      const submit = form.querySelector('[type="submit"]');
      if (submit) submit.setAttribute("aria-disabled", "true");

      try {
        const formData = new FormData(form);
        const response = await fetch(window.themeRoutes?.cartAddUrl || "/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });

        if (!response.ok) throw new Error("Add failed");

        await this.refresh();
        this.open();
      } catch (error) {
        console.error(error);
        window.location.href = form.getAttribute("action") || "/cart";
      } finally {
        if (submit) submit.removeAttribute("aria-disabled");
      }
    },

    async changeLine(key, quantity) {
      try {
        const response = await fetch(window.themeRoutes?.cartChangeUrl || "/cart/change.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: key, quantity }),
        });

        if (!response.ok) throw new Error("Change failed");
        await this.refresh();
      } catch (error) {
        console.error(error);
      }
    },

    async refresh() {
      try {
        const response = await fetch(window.themeRoutes?.cartUrl || "/cart.js", {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error("Cart fetch failed");
        const cart = await response.json();
        this.render(cart);
      } catch (error) {
        console.error(error);
      }
    },

    money(cents) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: window.themeCurrency || "USD",
        }).format(cents / 100);
      } catch {
        return `$${(cents / 100).toFixed(2)}`;
      }
    },

    safeUrl(url) {
      if (!url || typeof url !== "string") return "";
      try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
        return parsed.href;
      } catch {
        return "";
      }
    },

    render(cart) {
      this.countEls.forEach((el) => {
        el.textContent = String(cart.item_count);
        el.setAttribute("data-count", String(cart.item_count));
      });

      if (!this.body || !this.footer) return;

      this.body.replaceChildren();

      if (!cart.items.length) {
        const empty = document.createElement("p");
        empty.className = "cart-drawer__empty";
        empty.textContent =
          this.root.getAttribute("data-empty-message") || "Your cart is empty.";
        this.body.appendChild(empty);
        this.footer.hidden = true;
        return;
      }

      this.footer.hidden = false;

      cart.items.forEach((item) => {
        const article = document.createElement("article");
        article.className = "cart-item";

        const imageUrl = this.safeUrl(item.image);
        if (imageUrl) {
          const img = document.createElement("img");
          img.className = "cart-item__image";
          img.src = imageUrl;
          img.alt = "";
          img.width = 72;
          img.height = 72;
          img.loading = "lazy";
          article.appendChild(img);
        } else {
          const placeholder = document.createElement("div");
          placeholder.className = "cart-item__image";
          placeholder.setAttribute("aria-hidden", "true");
          article.appendChild(placeholder);
        }

        const details = document.createElement("div");

        const title = document.createElement("a");
        title.className = "cart-item__title";
        title.href = this.safeUrl(item.url) || "/cart";
        title.textContent = item.product_title || "Item";
        details.appendChild(title);

        const meta = document.createElement("div");
        meta.className = "cart-item__meta";
        const variant =
          item.variant_title && item.variant_title !== "Default Title"
            ? `${item.variant_title} · `
            : "";
        meta.textContent = `${variant}Qty ${item.quantity} · ${this.money(item.final_line_price)}`;
        details.appendChild(meta);

        const remove = document.createElement("button");
        remove.className = "cart-item__remove button button--text";
        remove.type = "button";
        remove.setAttribute("data-cart-remove", String(item.key || ""));
        remove.textContent = "Remove";
        details.appendChild(remove);

        article.appendChild(details);
        this.body.appendChild(article);
      });

      const subtotal = this.footer.querySelector("[data-cart-subtotal]");
      if (subtotal) subtotal.textContent = this.money(cart.total_price);
    },
  };

  document.addEventListener("DOMContentLoaded", () => {
    CartDrawer.init();
    HeroMotion.init();
    Reveal.init();
    ProductPage.init();
  });

  const HeroMotion = {
    pin: null,
    hero: null,
    layers: null,
    metaItems: null,
    ticking: false,

    init() {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      this.pin = document.querySelector("[data-hero-pin]");
      this.hero = document.querySelector("[data-hero]");
      if (!this.pin || !this.hero) return;

      this.layers = {};
      this.hero.querySelectorAll("[data-parallax]").forEach((el) => {
        this.layers[el.getAttribute("data-parallax")] = el;
      });
      this.metaItems = this.hero.querySelectorAll(".hero__meta-item");

      window.addEventListener("scroll", () => this.requestTick(), { passive: true });
      window.addEventListener("resize", () => this.requestTick(), { passive: true });
      this.update();
    },

    requestTick() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    },

    clamp(value, min = 0, max = 1) {
      return Math.min(max, Math.max(min, value));
    },

    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    },

    set(el, props) {
      if (!el) return;
      Object.entries(props).forEach(([key, value]) => {
        el.style.setProperty(key, String(value));
      });
    },

    update() {
      const pinRect = this.pin.getBoundingClientRect();
      const scrollable = Math.max(1, this.pin.offsetHeight - window.innerHeight);
      const raw = this.clamp(-pinRect.top / scrollable);
      const p = this.easeOutCubic(raw);
      const pFast = this.clamp(raw * 1.35);
      const pLate = this.clamp((raw - 0.12) / 0.88);

      this.set(this.layers["glow-a"], {
        "--p-x": -80 * p,
        "--p-y": 120 * p,
        "--p-s": 1 + 0.35 * p,
        "--p-o": 1 - 0.45 * p,
      });
      this.set(this.layers["glow-b"], {
        "--p-x": 90 * p,
        "--p-y": -100 * p,
        "--p-s": 1 + 0.45 * p,
        "--p-o": 1 - 0.35 * p,
      });
      this.set(this.layers.grid, {
        "--p-y": 60 * p,
        "--p-s": 1 + 0.22 * p,
        "--p-o": 1 - 0.75 * pFast,
      });
      this.set(this.layers["orb-1"], {
        "--p-x": 140 * p,
        "--p-y": -90 * p,
        "--p-s": 1 - 0.15 * p,
        "--p-o": 1 - pFast,
        "--p-r": `${12 + 18 * p}deg`,
      });
      this.set(this.layers["orb-2"], {
        "--p-x": -110 * p,
        "--p-y": 130 * p,
        "--p-s": 1 + 0.25 * p,
        "--p-o": 1 - pFast,
        "--p-r": `${-14 - 22 * p}deg`,
      });

      this.set(this.layers.brand, {
        "--p-y": -40 * pFast,
        "--p-o": 1 - this.clamp(raw * 2.2),
        "--p-b": 8 * pFast,
      });
      this.set(this.layers["line-1"], {
        "--p-x": -48 * p,
        "--p-y": -90 * p,
        "--p-s": 1 + 0.12 * p,
        "--p-o": 1 - this.clamp(raw * 1.4),
        "--p-b": 10 * pLate,
      });
      this.set(this.layers["line-2"], {
        "--p-x": 56 * p,
        "--p-y": -130 * pLate,
        "--p-s": 1 + 0.18 * pLate,
        "--p-o": 1 - this.clamp((raw - 0.08) * 1.5),
        "--p-b": 12 * this.clamp((raw - 0.2) / 0.8),
      });
      if (this.layers["line-1"]?.parentElement?.classList.contains("hero__heading")) {
        this.set(this.layers["line-1"].parentElement, {
          "--p-track": 0.035 * p,
        });
      }
      this.set(this.layers.sub, {
        "--p-y": 70 * p,
        "--p-o": 1 - this.clamp(raw * 1.8),
        "--p-b": 6 * pFast,
        "--p-s": 1 - 0.04 * p,
      });
      this.set(this.layers.cta, {
        "--p-y": 110 * p,
        "--p-o": 1 - this.clamp(raw * 2),
        "--p-b": 8 * pFast,
      });
      this.set(this.layers.meta, {
        "--p-y": 40 * p,
        "--p-o": 1 - this.clamp(raw * 1.6),
      });

      this.metaItems.forEach((item, index) => {
        const dir = index - (this.metaItems.length - 1) / 2;
        this.set(item, {
          "--p-x": dir * 70 * p,
          "--p-y": 30 * p,
          "--p-o": 1 - this.clamp(raw * 1.7),
        });
      });

      this.set(this.layers.progress, {
        "--p-s": raw,
      });
      this.set(this.layers.hint, {
        "--p-y": 24 * pFast,
        "--p-o": 1 - this.clamp(raw * 3.5),
      });
    },
  };

  const ProductPage = {
    root: null,
    product: null,
    optionInputs: null,

    init() {
      this.root = document.querySelector("[data-product]");
      if (!this.root) return;

      const jsonEl = this.root.querySelector("[data-product-json]");
      if (!jsonEl) return;

      try {
        this.product = JSON.parse(jsonEl.textContent);
      } catch {
        return;
      }

      this.optionInputs = this.root.querySelectorAll("[data-option-input]");
      this.optionInputs.forEach((input) => {
        input.addEventListener("change", () => this.syncVariant());
      });

      this.root.querySelectorAll("[data-thumb]").forEach((thumb) => {
        thumb.addEventListener("click", () => this.showMedia(thumb));
      });

      this.syncVariant();
    },

    selectedOptions() {
      const values = [];
      this.root.querySelectorAll("[data-option-index]").forEach((fieldset) => {
        const checked = fieldset.querySelector("[data-option-input]:checked");
        values.push(checked ? checked.value : null);
      });
      return values;
    },

    findVariant(options) {
      return (this.product.variants || []).find((variant) =>
        options.every((value, index) => variant[`option${index + 1}`] === value)
      );
    },

    money(cents) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: window.themeCurrency || "USD",
        }).format(cents / 100);
      } catch {
        return `$${(cents / 100).toFixed(2)}`;
      }
    },

    syncVariant() {
      const options = this.selectedOptions();
      if (!options.length) return;

      const variant = this.findVariant(options);
      const idInput = this.root.querySelector("[data-variant-id]");
      const priceEl = this.root.querySelector("[data-product-price]");
      const addBtn = this.root.querySelector("[data-add-button]");
      const addLabel = this.root.querySelector("[data-add-label]");

      this.root.querySelectorAll("[data-option-index]").forEach((fieldset, index) => {
        const label = fieldset.querySelector("[data-option-label]");
        if (label && options[index]) label.textContent = options[index];
      });

      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = "Unavailable";
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = this.money(variant.price);

      if (addBtn && addLabel) {
        if (variant.available) {
          addBtn.disabled = false;
          addLabel.textContent = "Add to cart";
        } else {
          addBtn.disabled = true;
          addLabel.textContent = "Sold out";
        }
      }

      if (variant.featured_image) {
        const src = variant.featured_image.src;
        const main = this.root.querySelector(".product-page__main-image, [data-product-main-image]");
        if (main && src) {
          main.src = src.startsWith("//") ? `https:${src}` : src;
          main.removeAttribute("srcset");
        }
        this.root.querySelectorAll("[data-thumb]").forEach((thumb) => {
          const match =
            String(variant.featured_image.id) === thumb.getAttribute("data-media-id") ||
            (thumb.getAttribute("data-media-src") || "").includes(
              String(variant.featured_image.id)
            );
          thumb.classList.toggle("is-active", Boolean(match));
        });
      }
    },

    showMedia(thumb) {
      const main = this.root.querySelector(".product-page__main-image, [data-product-main-image]");
      if (!main) return;
      const src = thumb.getAttribute("data-media-src");
      const srcset = thumb.getAttribute("data-media-srcset");
      if (src) main.src = src;
      if (srcset) main.srcset = srcset;
      this.root.querySelectorAll("[data-thumb]").forEach((el) => el.classList.remove("is-active"));
      thumb.classList.add("is-active");
    },
  };

  const Reveal = {
    init() {
      const nodes = document.querySelectorAll("[data-reveal]");
      if (!nodes.length) return;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        nodes.forEach((el) => el.classList.add("is-visible"));
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          });
        },
        { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
      );

      nodes.forEach((el) => io.observe(el));
    },
  };
})();
